'use client';

import { c as _c } from "react/compiler-runtime";
import type { EmblaCarouselType } from 'embla-carousel';
import { useRef, useState } from 'react';
import { Button } from '@/app/components/button';
import { ButtonGroup } from '@/app/components/button-group';
import { IconArrowBack } from '@/app/components/icons/ui-actions/icon-arrow-back';
import { IconArrowForward } from '@/app/components/icons/ui-actions/icon-arrow-forward';
import { Container } from '@/app/components/layouts/container';
import { Section } from '@/app/components/layouts/section';
import { PaginationDash } from '@/app/components/navigation/pagination';
import { Sliders } from '@/app/components/sliders';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import { IsuzuCareTestimonialCard, type IsuzuCareTestimonialItemProps } from '@/app/cv/components/sections/service/isuzu-care/testimonial-section/card';
import { useInView } from '@/app/hooks/useInView';
import { useRevealAnim } from '@/app/hooks/useRevealAnim';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
export interface IsuzuCareTestimonialProps {
  className?: string;
  lead?: string;
  title: string;
  items?: IsuzuCareTestimonialItemProps[];
}
export const IsuzuCareTestimonial = t0 => {
  const $ = _c(52);
  const {
    className,
    lead,
    title,
    items: t1
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const items = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      defaultBaseDelay: 0.04
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  useRevealAnim(t3);
  const containerRef = useRef(null);
  const [emblaApi, setEmblaApi] = useState(null);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      rootMargin: "0px 0px -40% 0px"
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const isInView = useInView(containerRef, t4);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextSlider(emblaApi);
  const {
    currentPage,
    onPageChange,
    scrollSnaps
  } = usePaginationSlider(emblaApi);
  let t5;
  if ($[4] !== className) {
    t5 = cn("bg-white", className);
    $[4] = className;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = createDataAttributes({
      test: "testimonial-section"
    });
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = createDataAttributes({
      test: "container"
    });
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== lead) {
    t8 = !!lead && <p className="text-body-1 font-noto-bold text-red-500" {...createDataAttributes({
      test: "lead"
    })}>{lead}</p>;
    $[8] = lead;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] !== title) {
    t9 = !!title && <h2 className="text-h2 font-display" dangerouslySetInnerHTML={{
      __html: title
    }} {...createDataAttributes({
      test: "title"
    })} />;
    $[10] = title;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = <div className="space-y-2">{t8}{t9}</div>;
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = createDataAttributes({
      test: "model-lineup-pagination"
    });
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <IconArrowBack />;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = createDataAttributes({
      test: "previous-button"
    });
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== onPrevButtonClick || $[19] !== prevBtnDisabled) {
    t14 = <Button aria-label="Previous" variant="primary" color="black" iconBefore={t12} disabled={prevBtnDisabled} onClick={onPrevButtonClick} {...t13} />;
    $[18] = onPrevButtonClick;
    $[19] = prevBtnDisabled;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <IconArrowForward />;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = createDataAttributes({
      test: "next-button"
    });
    $[22] = t16;
  } else {
    t16 = $[22];
  }
  let t17;
  if ($[23] !== nextBtnDisabled || $[24] !== onNextButtonClick) {
    t17 = <Button aria-label="Next" variant="primary" color="black" iconBefore={t15} disabled={nextBtnDisabled} onClick={onNextButtonClick} {...t16} />;
    $[23] = nextBtnDisabled;
    $[24] = onNextButtonClick;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t14 || $[27] !== t17) {
    t18 = <ButtonGroup className="mt-10 hidden justify-end md:flex" {...t11}>{t14}{t17}</ButtonGroup>;
    $[26] = t14;
    $[27] = t17;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t10 || $[30] !== t18) {
    t19 = <Container {...t7}><div className="flex items-center justify-between gap-8" data-revealanim={true}>{t10}{t18}</div></Container>;
    $[29] = t10;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = createDataAttributes({
      test: "slider-container"
    });
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== isInView || $[34] !== items) {
    let t22;
    if ($[36] !== isInView) {
      t22 = (item, index) => <div key={index} className={cn("property-opacity-translate ease-bounce w-full shrink-0 basis-78 duration-1000 sm:basis-96 md:basis-110 lg:basis-219", {
        "translate-12 opacity-0": !isInView
      })} {...createDataAttributes({
        test: `item-${index}`
      })}><IsuzuCareTestimonialCard {...item} index={index} /></div>;
      $[36] = isInView;
      $[37] = t22;
    } else {
      t22 = $[37];
    }
    t21 = items.map(t22);
    $[33] = isInView;
    $[34] = items;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[38] !== t21) {
    t22 = <Sliders loop={false} align="start" hidden={false} containerClassName="gap-x-4 lg:gap-x-6" onEmblaInit={setEmblaApi}>{t21}</Sliders>;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] !== currentPage || $[41] !== items.length || $[42] !== onPageChange || $[43] !== scrollSnaps) {
    t23 = items.length > 1 && <div className={cn("mt-8 md:hidden lg:mt-10")}><PaginationDash theme="dark" wrapperClassName="justify-center" totalPages={scrollSnaps.length} currentPage={currentPage} onPageChange={onPageChange} /></div>;
    $[40] = currentPage;
    $[41] = items.length;
    $[42] = onPageChange;
    $[43] = scrollSnaps;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  let t24;
  if ($[45] !== t22 || $[46] !== t23) {
    t24 = <Container className="relative mt-6 lg:mt-16" ref={containerRef} {...t20}>{t22}{t23}</Container>;
    $[45] = t22;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== t19 || $[49] !== t24 || $[50] !== t5) {
    t25 = <Section size="md" className={t5} {...t6} data-revealanim-group={true}>{t19}{t24}</Section>;
    $[48] = t19;
    $[49] = t24;
    $[50] = t5;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  return t25;
};